// TTSVoiceCallScreen.tsx
import React, { useEffect, useState, useRef } from 'react';
import { Bubble } from '@ant-design/x';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/store/storeTTS';
import ToolBar from '../ToolBar';
import CameraArea from '../CameraArea';
import styles from './index.module.less';
import AudioController from '../AudioController';
import Subtitle from '@/assets/img/Subtitle.png'
import SubtitleClose from '@/assets/img/SubtitleClose.png'
import { getRoles } from '@/utils/Page';
import { Display } from '@/components/Display/Display';
import flipMic from '@/assets/img/FlipMic.png'
import { useDeviceState } from '@/libTTS/useCommon';
import { updateSelectedDevice } from '@/store/storeTTS/slices/device';
import RtcClient from '@/libTTS/RtcClient';
import VERTC, { MediaType, MirrorType } from '@volcengine/rtc';
import Utils from '@/utils/utilsTTS/utils';
interface TTSVoiceCallScreenProps {
  onBackToChat: () => void;
  ttsMessagesRef: React.MutableRefObject<any[]>;
  currentAgent: any; // 或者定义具体类型
  sessionId: any;
  listRef: any;
  getItem: any;
  sessionMap: any;
  ttsContentRef: any;
  TTSNewMsg: any
  getItemTTS: any;
  TTSNewlist: any
  resetVoiceCallStatus: any;
  originMsgMapRef: any;
  hideTTSActions: any
  setHideTTSActions: any
}

const TTSVoiceCallScreen: React.FC<TTSVoiceCallScreenProps> = ({
  onBackToChat,
  ttsMessagesRef,
  currentAgent,
  sessionId,
  listRef,
  getItem,
  sessionMap,
  TTSNewMsg,
  getItemTTS,
  TTSNewlist,
  resetVoiceCallStatus,
  ttsContentRef,
  originMsgMapRef,
  hideTTSActions,
  setHideTTSActions,
}) => {
  const [showSubtitle, setShowSubtitle] = useState(false);
  const [hideAvatarAndText, setHideAvatarAndText] = useState(false);
  const subtitleContainerRef = useRef<HTMLDivElement>(null);
  const [showCameraFeed, setShowCameraFeed] = useState(false);
  const [cameraInitialized, setCameraInitialized] = useState(false);
  const dispatch = useDispatch();
  const { switchCamera } = useDeviceState();
  const deviceState = useSelector((state: RootState) => state.device);
  const { selectedCamera } = deviceState;
  // 添加状态来跟踪按钮点击
  const micClickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cameraClickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cameraFacingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // 拖拽相关状态
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const cameraRef = useRef<HTMLDivElement>(null);
  // 摄像头窗口尺寸
  const CAMERA_WINDOW_WIDTH = 100;
  const CAMERA_WINDOW_HEIGHT = 160;
  // 计算初始位置
  const getInitialPosition = () => {
    const screenWidth = window.innerWidth;
    const x = screenWidth - CAMERA_WINDOW_WIDTH - 16; // 16px 边距
    const y = 70; // top 70px
    return { x, y };
  };

  const toggleSubtitle = () => {
    const wasShowingSubtitle = showSubtitle;
    setShowSubtitle(!showSubtitle);
    setHideAvatarAndText(!hideAvatarAndText);

    if (!wasShowingSubtitle) {
      // 即将开启字幕，设置摄像头窗口到右边top 70px位置
      const initialPos = getInitialPosition();
      setPosition(initialPos);
      document.body.classList.add('tts-subtitle-enabled');
    } else {
      // 即将关闭字幕，移除类名
      document.body.classList.remove('tts-subtitle-enabled');
    }
  };  // 切换摄像头视频展示
  const toggleCameraFeed = () => {
    setShowCameraFeed(!showCameraFeed);
    if (!showCameraFeed) {
      setCameraInitialized(false)
    }
  };
  // 处理视频窗口点击事件，用于在小窗模式下恢复全屏
  const handleVideoClick = () => {
    console.log('点击了视频窗口');
    if (showCameraFeed && showSubtitle) {
      // 当前为小窗模式，切换回全屏模式（关闭字幕）
      toggleSubtitle();
      // 重置位置到初始状态
      setPosition({ x: 0, y: 0 });
    }
  };
  // 添加一个效果，在切换到全屏模式时重置位置
  useEffect(() => {
    if (!showSubtitle && showCameraFeed) {
      // 当从小窗模式切换回全屏模式时，重置位置
      setPosition({ x: 0, y: 0 });
    }
  }, [showSubtitle, showCameraFeed]);
  // 当开启摄像头和字幕时，设置初始位置到右边top 70px
  useEffect(() => {
    if (showCameraFeed && showSubtitle) {
      const initialPos = getInitialPosition();
      setPosition(initialPos);
    }
  }, [showCameraFeed, showSubtitle]);

  // 边界限制函数
  const constrainToBoundary = (x: number, y: number) => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    // 计算边界限制
    const minX = 0;
    const minY = 0;
    const maxX = screenWidth - CAMERA_WINDOW_WIDTH;
    const maxY = screenHeight - CAMERA_WINDOW_HEIGHT;

    // 限制在边界内
    const constrainedX = Math.max(minX, Math.min(x, maxX));
    const constrainedY = Math.max(minY, Math.min(y, maxY));

    return { x: constrainedX, y: constrainedY };
  };

  // 处理拖拽开始
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (!showCameraFeed || !showSubtitle) return;

    e.preventDefault();
    e.stopPropagation();

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    setIsDragging(true);
    setDragOffset({
      x: clientX - position.x,
      y: clientY - position.y
    });

    // 添加拖拽样式
    if (cameraRef.current) {
      cameraRef.current.style.transition = 'none';
      cameraRef.current.style.cursor = 'grabbing';
    }
  };
  // 处理拖拽移动
  const handleDragMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging || !showCameraFeed) return;

    e.preventDefault();
    e.stopPropagation();

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    // 计算新位置
    let newX = clientX - dragOffset.x;
    let newY = clientY - dragOffset.y;

    // 应用边界限制
    const constrainedPos = constrainToBoundary(newX, newY);

    setPosition(constrainedPos);
  };

  // 处理鼠标/触摸结束事件
  const handleDragEnd = (e: MouseEvent | TouchEvent) => {
    // // 阻止默认行为
    // e.preventDefault();
    // e.stopPropagation();

    setIsDragging(false);
    // 恢复样式
    if (cameraRef.current) {
      cameraRef.current.style.transition = 'transform 0.2s ease';
      cameraRef.current.style.cursor = 'grab';
    }
  };

  // 添加事件监听器
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove, { passive: false });
      document.addEventListener('mouseup', handleDragEnd, { passive: false });
      document.addEventListener('touchmove', handleDragMove, { passive: false });
      document.addEventListener('touchend', handleDragEnd, { passive: false });

      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
        document.removeEventListener('touchmove', handleDragMove);
        document.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, dragOffset, showCameraFeed, showSubtitle]);
  // 窗口大小变化时重新计算边界
  useEffect(() => {
    const handleResize = () => {
      if (showCameraFeed && showSubtitle) {
        const constrainedPos = constrainToBoundary(position.x, position.y);
        setPosition(constrainedPos);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [position, showCameraFeed, showSubtitle]);
  // 清理高并发函数
  useEffect(() => {
    return () => {
      // 清理所有定时器
      if (micClickTimeoutRef.current) {
        clearTimeout(micClickTimeoutRef.current);
      }
      if (cameraClickTimeoutRef.current) {
        clearTimeout(cameraClickTimeoutRef.current);
      }
      if (cameraFacingTimeoutRef.current) {
        clearTimeout(cameraFacingTimeoutRef.current);
      }
    };
  }, []);

  // 切换前后摄像头
  const switchCameraFacing = async (): Promise<undefined> => {
    try {
      // 对于移动设备，可以直接使用"user"和"environment"来切换摄像头
      // 当前是后置摄像头，切换到前置摄像头
      if (selectedCamera === 'environment') {
        // 切换到前置摄像头
        await RtcClient.stopVideoCapture();
        dispatch(updateSelectedDevice({ selectedCamera: 'user' }));
        RtcClient.switchDevice(MediaType.VIDEO, 'user');
        RtcClient.setLocalVideoMirrorType(MirrorType.MIRROR_TYPE_RENDER);
        await RtcClient.startVideoCapture('user');
      } else {
        // 切换到后置摄像头（主摄）
        await switchToBackCamera();
      }
      return;
    } catch (error: any) {
      // 如果是并发操作错误，则不处理
      if (error?.message === 'Camera switch is in progress') {
        return;
      }
      console.error('切换摄像头失败:', error);
    }
  };

  // 专门切换到后置摄像头的函数
  const switchToBackCamera = async (): Promise<void> => {
    await RtcClient.stopVideoCapture();

    const devices = await VERTC.enumerateVideoCaptureDevices();
    const videoDevices = devices.filter(device => device.kind === 'videoinput');

    let targetDeviceId = 'environment'; // 默认使用'environment'

    if (videoDevices.length > 0) {
      // 查找后置摄像头（主摄）
      const backCamera = videoDevices.filter(device => {
        const label = device.label.toLowerCase();
        return label.includes('back') ||
          label.includes('rear') ||
          label.includes('后') ||
          label.includes('后置') 
      });
      const mainBackCamera = backCamera.find(device => {
        const label = device.label.toLowerCase();
        return label.includes('main') ||
          label.includes('primary');
      });
      targetDeviceId = mainBackCamera ? mainBackCamera.deviceId : 'environment';
    }

    dispatch(updateSelectedDevice({ selectedCamera: targetDeviceId }));
    RtcClient.switchDevice(MediaType.VIDEO, targetDeviceId);
    RtcClient.setLocalVideoMirrorType(MirrorType.MIRROR_TYPE_NONE);
    await RtcClient.startVideoCapture(targetDeviceId);
  };

  useEffect(() => {
    if (showCameraFeed && !cameraInitialized) {
      // 确保摄像头视频已初始化
      setTimeout(() => {
        setCameraInitialized(true);
      }, 100);
    }
  }, [showCameraFeed, cameraInitialized]);
  // 当消息更新时自动滚动到底部
  useEffect(() => {
    if (showSubtitle && subtitleContainerRef.current) {
      // 等待DOM更新后滚动到底部
      setTimeout(() => {
        if (subtitleContainerRef.current) {
          subtitleContainerRef.current.scrollTop = subtitleContainerRef.current.scrollHeight;
        }
      }, 0);
    }
  }, [getItem, showSubtitle]);

  return (
    <div className={styles['tts-call-screen']}>
      {/* 视频背景展示 */}
      <Display show={showCameraFeed}>
        <div
          ref={cameraRef}
          style={{
            position: 'absolute',
            width: showSubtitle ? `${CAMERA_WINDOW_WIDTH}px` : '100%',
            height: showSubtitle ? `${CAMERA_WINDOW_HEIGHT}px` : '100%',
            zIndex: 1,
            margin: showSubtitle ? '0' : '0',
            transform: `translate(${position.x}px, ${position.y}px)`,
            cursor: showCameraFeed && showSubtitle ? (isDragging ? 'grabbing' : 'grab') : 'default',
            userSelect: 'none',
            transition: isDragging ? 'none' : 'transform 0.2s ease',
            left: 0,
            top: 0
          }}
          onClick={handleVideoClick}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          <CameraArea />
        </div>
      </Display>
      {/* 半透明遮罩，确保内容可读性 */}
      {/* <Display show={showCameraFeed}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 2
        }} />
      </Display> */}

      <div className={styles['tts-header']} style={{ position: 'relative', zIndex: 3 }}>
        <Display show={showSubtitle || showCameraFeed}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'absolute', top: '15px', left: '15px' }}>
            <img src={currentAgent?.agent_icon} alt="" className={styles['ai-avatar']} />
            <AudioController hideAudioLoading />
          </div>
        </Display>
        <Display show={showCameraFeed}>
          <div className={styles['tts-header-buttons']} onClick={() => {
            // 防止重复点击
            if (cameraFacingTimeoutRef.current) {
              return;
            }

            cameraFacingTimeoutRef.current = setTimeout(() => {
              switchCameraFacing();
              if (cameraFacingTimeoutRef.current) {
                clearTimeout(cameraFacingTimeoutRef.current);
                cameraFacingTimeoutRef.current = null;
              }
            }, 1000);
          }}>
            <img src={flipMic} alt="" style={{ width: '22px', height: '22px' }} />
          </div>
        </Display>

        <div className={styles['tts-buttons']} onClick={toggleSubtitle}>
          <img src={showSubtitle ? SubtitleClose : Subtitle} alt={showSubtitle ? "关闭字幕" : "开启字幕"} className={styles['tts-buttons-img']} />
          <span className={styles['tts-buttons-size']}>{showSubtitle ? "关闭字幕" : "开启字幕"}</span>
        </div>
      </div>
      {/* 1. AI 角色头像 */}
      <Display show={!hideAvatarAndText && !showCameraFeed}>
        <div className={styles['avatar-container']}>
          <div className={styles['avatar-wrapper']}>
            <img
              src={currentAgent?.agent_figure}
              alt="AI Agent"
              className={styles['avatar']}
            />
          </div>
        </div>
      </Display>

      {/* 2. 提示文字 */}
      <Display show={!hideAvatarAndText && !showCameraFeed}>
        <div className={styles['tts-size-text']}>
          <AudioController />
        </div>
      </Display>

      {/* 字幕区域 */}
      <Display show={showSubtitle}>
        <div
          ref={subtitleContainerRef}
          className={styles['subtitle-text']}
          style={{ height: '70%', overflowY: 'auto', width: '100%' }}
        >
          <Bubble.List
            autoScroll={sessionId ? true : false}
            items={TTSNewlist}
            roles={getRoles(sessionMap?.[sessionId]?.loading) as any}
            className={`${styles['chats-messages']}`}
            style={{ height: '100%' }}
          />
        </div>
      </Display>
      <div className={styles['spacer']} style={{ position: 'relative', zIndex: 3 }}></div>
      {/* 3. 工具栏 */}
      <div className={styles['tts-toolbar']} style={{ position: 'relative', zIndex: 3 }}>
        <ToolBar
          onBackToChat={onBackToChat}
          ttsMessagesRef={ttsMessagesRef}
          onCameraToggle={toggleCameraFeed}
          TTSNewMsg={TTSNewMsg}
          resetVoiceCallStatus={resetVoiceCallStatus}
          ttsContentRef={ttsContentRef}
          originMsgMapRef={originMsgMapRef}
          sessionId={sessionId}
          hideTTSActions={hideTTSActions}
          setHideTTSActions={setHideTTSActions}
        />
      </div>
    </div >
  );
};

export default TTSVoiceCallScreen;
